import { useLocation, useParams } from "react-router-dom";
import { useAuthStore } from "../../../atoms/user.auth.store";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import { DbFileListContainer } from "../../../style/contents/db/db";
import UserInfo from "../../../components/backup/user.info";
import SearchForm from "../../../components/common/search.form";
import { useEffect, useState } from "react";
import { fetchBackupUserListLoad, fetchDbBackupList } from "../../../api/admin";
import DbFileListTable from "../../../components/backup/db.file.list.table";
import type { BackupUser } from "../backup/backup.user";
import { fetchLoadClientLogin } from "../../../api/auth";

export interface DbBackupFileItem {
  backupId: number;
  userId: number;
  userName: string;
  userAccount: string;
  fileName: string;
  deviceId: string;
  type: "disk" | string;
  size: number;
  s3Url: string;
  backupStamp: string;
  createdDate: string;
  fileId?: number;
  backupHistoryId?: number;
  isWorm?: boolean;
}

const decodeBase64Param = (value: string | null) => {
  if (!value) return "";

  try {
    return decodeURIComponent(atob(value));
  } catch {
    return decodeURIComponent(value);
  }
};

export interface DbFileContext {
  userId: number;
  deviceId: string;
  userAccount: string;
  pcName: string;
}

const DbFileList = ({ context }: { context?: DbFileContext }) => {
  const { role } = useAuthStore();

  const [fileList, setFileList] = useState<DbBackupFileItem[]>([]);
  const [userData, setUserData] = useState<BackupUser[]>([]);
  const [loginToken, setLoginToken] = useState("");

  const [searchKeyword, setSearchKeyword] = useState("");
  const [pageSize, setPageSize] = useState("10");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const { path: rawDeviceId = "" } = useParams<{ path: string }>();
  const deviceId = context?.deviceId ?? decodeURIComponent(rawDeviceId);
  const id = context ? String(context.userId) : queryParams.get("id");
  const userAccount = context?.userAccount ?? queryParams.get("userAccount");
  const pcName =
    context?.pcName ?? decodeBase64Param(queryParams.get("pcName"));
  const rawDirectoryInfo = localStorage.getItem("directory");
  const directoryInfo = rawDirectoryInfo ? JSON.parse(rawDirectoryInfo) : null;
  const adminUserId = localStorage.getItem("userId");
  const filteredFileList = fileList.filter(
    (file) => file.userId === Number(id) && file.deviceId === deviceId,
  );

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    // TODO: fetchData(keyword, pageSize);
  };

  const handlePageSizeChange = (size: string) => {
    setPageSize(size);
  };

  useEffect(() => {
    if (role !== "admin") return;

    const loadUserList = async () => {
      const userId = localStorage.getItem("userId");
      const companyCode = localStorage.getItem("companyCode");

      try {
        const response = await fetchBackupUserListLoad(
          Number(userId),
          companyCode || "",
        );
        setUserData(response.userList);
      } catch (err) {
        console.error("사용자 목록 로딩 실패:", err);
      }
    };

    loadUserList();
  }, [role]);

  useEffect(() => {
    if (role !== "admin" || userData.length === 0 || !userAccount || !id) {
      return;
    }

    const loadUserLogin = async () => {
      const companyCode = localStorage.getItem("companyCode");
      const matchedUser = userData.find(
        (user) => user.userAccount === userAccount,
      );

      if (!matchedUser) {
        console.warn("해당 userAccount에 대한 유저 정보를 찾을 수 없습니다.");
        setLoginToken("");
        return;
      }

      try {
        const response = await fetchLoadClientLogin({
          companyCode: companyCode || "",
          userAccount: matchedUser.userAccount,
          password: matchedUser.password,
          deviceId: "",
        });

        setLoginToken(response.jwtToken);
      } catch (err) {
        console.error("로그인 실패:", err);
        setLoginToken("");
      }
    };

    loadUserLogin();
  }, [id, role, userAccount, userData]);

  useEffect(() => {
    const loadFileList = async () => {
      if (!deviceId || !id || !adminUserId) return;

      setIsLoading(true);
      setLoadError(false);
      try {
        const response = await fetchDbBackupList(adminUserId);
        setFileList(response.backupList || []);
      } catch (err) {
        setLoadError(true);
        console.error("파일 목록 조회 실패:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (role === "admin") loadFileList();
    else setIsLoading(false);
  }, [adminUserId, deviceId, id, role, retry]);

  console.log(deviceId, pcName);
  console.log(context);

  return (
    <DbFileListContainer>
      {!context && (
        <>
          <ContentsTopHeader
            title="디바이스 목록  > DB 백업 파일 목록"
            backArrowPath="-1"
          />
          <div className="contents-top">
            <div className="device-name">
              <p>
                {pcName}({deviceId})
              </p>
            </div>
            <div className="device-info">
              <p>마지막 백업시간 : {directoryInfo?.createDate || "-"}</p>
              {role === "admin" && <UserInfo userAccount={userAccount || ""} />}
            </div>
          </div>
        </>
      )}
      <div className="file-list-container">
        <div className="top">
          <div className="left">{pcName + `(` + deviceId + `)`}</div>
          <div className="right">
            <SearchForm
              onSearch={handleSearch}
              onPageSizeChange={handlePageSizeChange}
              pageSize={pageSize}
              pageSizeHidden={!!context}
            />
          </div>
        </div>
        {loadError ? (
          <div role="alert">
            DB 백업 파일 목록을 불러오지 못했습니다.{" "}
            <button
              type="button"
              onClick={() => setRetry((value) => value + 1)}
            >
              다시 시도
            </button>
          </div>
        ) : filteredFileList ? (
          <DbFileListTable
            data={filteredFileList}
            searchKeyword={searchKeyword}
            pageSize={parseInt(pageSize)}
            paginationEnabled={!context}
            isLoading={isLoading}
            userId={id || ""}
            loginToken={loginToken}
          />
        ) : (
          <div>
            <p>로딩 중입니다.</p>
          </div>
        )}
      </div>
    </DbFileListContainer>
  );
};

export default DbFileList;
